// Botón 💬 de la barra superior (admin, tienda y chofer): abre un formulario
// para avisar de un error o mandar una sugerencia. Se manda solo, además de
// lo que escribe el usuario, la pantalla donde está y el tamaño de la
// ventana; el navegador lo toma el servidor del request.
//
// El formulario va en un portal al body: la barra lleva backdrop-filter, y
// eso vuelve a la barra el contenedor de cualquier position: fixed de adentro
// (el velo quedaba del tamaño de la barra).

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Button from "../ui/Button";
import { useAuth } from "../../context/AuthContext";
import { useFocusTrap } from "../../hooks/useFocusTrap";
import { authFetch } from "../../lib/authFetch";
import { FEEDBACK_MAX_MESSAGE, FeedbackKind, shrinkImage } from "../../lib/feedback";

type FeedbackButtonProps = {
  // Cada barra tiene su tamaño de botón: se le pasa el estilo de sus vecinos
  // (sin él, el de la barra del admin).
  buttonStyle?: React.CSSProperties;
};

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1000,
  background: "rgba(0,0,0,0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
};

const dialogStyle: React.CSSProperties = {
  width: "min(520px, 100%)",
  maxHeight: "calc(100vh - 32px)",
  overflowY: "auto",
  borderRadius: "20px",
  border: "1px solid var(--border)",
  background: "var(--background)",
  color: "var(--text-primary)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
  padding: "20px",
  display: "flex",
  flexDirection: "column",
  gap: "14px",
};

const kindButtonStyle = (active: boolean): React.CSSProperties => ({
  flex: 1,
  padding: "12px",
  borderRadius: "14px",
  cursor: "pointer",
  font: "inherit",
  fontSize: "14px",
  fontWeight: active ? 800 : 600,
  border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
  background: active ? "var(--primary-soft, rgba(79,140,255,0.12))" : "var(--surface-alt)",
  color: "var(--text-primary)",
});

const textareaStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "120px",
  resize: "vertical",
  boxSizing: "border-box",
  borderRadius: "12px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  padding: "10px 12px",
  font: "inherit",
  fontSize: "14px",
  lineHeight: 1.5,
};

const mutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };

const defaultButtonStyle: React.CSSProperties = {
  width: "46px",
  height: "46px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  fontSize: "18px",
};

const PLACEHOLDER: Record<FeedbackKind, string> = {
  ERROR: "Qué estabas haciendo, qué esperabas que pasara y qué pasó.",
  SUGERENCIA: "Qué te gustaría que haga el sistema, o qué cambiarías.",
};

export default function FeedbackButton({ buttonStyle }: FeedbackButtonProps) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<FeedbackKind | null>(null);
  const [message, setMessage] = useState("");
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // El velo cierra solo si el toque empezó y terminó en él: seleccionar texto
  // arrastrando desde el mensaje hasta afuera también dispara un click en el
  // velo, y cerraba borrando lo escrito.
  const pressedOverlay = useRef(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, open);
  // Cada captura que se prepara lleva un número; cerrar (o adjuntar otra)
  // lo cambia, y una que termina tarde ya no se pega: antes, cerrar mientras
  // decía «Preparando…» dejaba esa captura puesta para la próxima vez.
  const attachSeq = useRef(0);

  const reset = () => {
    attachSeq.current += 1;
    setPreparing(false);
    setKind(null);
    setMessage("");
    setScreenshot(null);
    setError("");
    setSent(false);
  };

  const close = () => {
    if (sending) return;
    setOpen(false);
    reset();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const attach = async (file: Blob | null | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("La captura tiene que ser una imagen."); return; }
    const mine = ++attachSeq.current;
    setPreparing(true);
    setError("");
    try {
      const image = await shrinkImage(file);
      if (mine === attachSeq.current) setScreenshot(image);
    } catch (e: any) {
      if (mine === attachSeq.current) setError(e?.message || "No se pudo leer la imagen");
    } finally {
      if (mine === attachSeq.current) setPreparing(false);
    }
  };

  // Ctrl+V de una captura (en la PC es lo más rápido: Impr Pant y pegar).
  const onPaste = (e: React.ClipboardEvent) => {
    const item = Array.from(e.clipboardData?.items || []).find((i) => i.type.startsWith("image/"));
    if (item) { e.preventDefault(); attach(item.getAsFile()); }
  };

  const canSend = !!kind && message.trim().length >= 3 && !sending && !preparing;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setError("");
    try {
      const res = await authFetch("/v2/feedback", {
        token,
        json: true,
        method: "POST",
        body: JSON.stringify({
          kind,
          message: message.trim(),
          page: `${window.location.pathname}${window.location.search}`,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          screenshot: screenshot || undefined,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.ok) throw new Error(body?.error || "No se pudo enviar. Probá de nuevo.");
      setSent(true);
    } catch (e: any) {
      setError(e instanceof TypeError ? "Sin conexión con el servidor. Probá de nuevo." : e?.message || "No se pudo enviar.");
    } finally {
      setSending(false);
    }
  };

  const dialog = (
    <div
      style={overlayStyle}
      onMouseDown={(e) => { pressedOverlay.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (pressedOverlay.current && e.target === e.currentTarget) close(); pressedOverlay.current = false; }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Sugerencias y errores"
        style={dialogStyle}
        onClick={(e) => e.stopPropagation()}
        onPaste={onPaste}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
          <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 800 }}>Sugerencias y errores</h3>
          <button type="button" onClick={close} aria-label="Cerrar" style={{ border: "none", background: "transparent", color: "var(--text-secondary)", fontSize: "20px", cursor: "pointer", lineHeight: 1 }}>
            ×
          </button>
        </div>

        {sent ? (
          <>
            <div style={{ fontSize: "15px", lineHeight: 1.5 }}>
              ¡Gracias! Lo recibimos y lo vamos a revisar.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={close}>Cerrar</Button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: "flex", gap: "10px" }}>
              <button type="button" style={kindButtonStyle(kind === "ERROR")} aria-pressed={kind === "ERROR"} onClick={() => setKind("ERROR")}>
                🐞 Algo anda mal
              </button>
              <button type="button" style={kindButtonStyle(kind === "SUGERENCIA")} aria-pressed={kind === "SUGERENCIA"} onClick={() => setKind("SUGERENCIA")}>
                💡 Tengo una sugerencia
              </button>
            </div>

            <div>
              <textarea
                aria-label="Mensaje"
                style={textareaStyle}
                value={message}
                maxLength={FEEDBACK_MAX_MESSAGE}
                placeholder={kind ? PLACEHOLDER[kind] : "Elegí arriba si es un error o una sugerencia, y contanos."}
                onChange={(e) => setMessage(e.target.value)}
              />
              <div style={{ ...mutedStyle, textAlign: "right" }}>{message.length}/{FEEDBACK_MAX_MESSAGE}</div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                aria-label="Adjuntar captura"
                style={{ display: "none" }}
                onChange={(e) => { attach(e.target.files?.[0]); e.target.value = ""; }}
              />
              <Button variant="secondary" onClick={() => fileRef.current?.click()} disabled={preparing || sending}>
                {preparing ? "Preparando…" : screenshot ? "Cambiar captura" : "📎 Adjuntar captura"}
              </Button>
              {screenshot ? (
                <>
                  <img src={screenshot} alt="Captura adjunta" style={{ height: "48px", borderRadius: "8px", border: "1px solid var(--border)" }} />
                  <button type="button" onClick={() => setScreenshot(null)} style={{ border: "none", background: "transparent", color: "var(--text-secondary)", cursor: "pointer", fontSize: "13px" }}>
                    Quitar
                  </button>
                </>
              ) : (
                <span style={mutedStyle}>Opcional. En la compu también podés pegarla con Ctrl+V.</span>
              )}
            </div>

            <div style={mutedStyle}>
              Junto con tu mensaje se envía tu usuario, la pantalla donde estás y el navegador.
            </div>

            {error ? <div role="alert" style={{ fontSize: "13px", color: "var(--danger, #d1242f)" }}>{error}</div> : null}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <Button variant="ghost" onClick={close} disabled={sending}>Cancelar</Button>
              <Button onClick={send} disabled={!canSend}>{sending ? "Enviando…" : "Enviar"}</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      <button
        type="button"
        style={buttonStyle ?? defaultButtonStyle}
        onClick={() => setOpen(true)}
        title="Sugerencias y errores"
        aria-label="Sugerencias y errores"
      >
        💬
      </button>
      {open ? createPortal(dialog, document.body) : null}
    </>
  );
}
